import {
  Autocomplete,
  Box,
  CircularProgress,
  FormHelperText,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC, useEffect, useMemo, useRef } from 'react';
import {
  Controller,
  FieldError,
  FieldValues,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { PermissionScope } from 'types/apps';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { getDisabledPermissionCopy } from 'utils/permissions';
import {
  CustomValidationType,
  HookFormComponentProps,
} from 'ui-component/HookFormComponents/types';
import _ from 'lodash';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import { useGoogleMapsScript } from 'hooks/useGoogleMapsScript';
import { parseAddressComponents } from 'utils/googlePlaces';

const getValidationColor = (theme: Theme, type?: string | undefined) => {
  switch (type) {
    case CustomValidationType.WARNING:
      return theme.palette.warning.main;
    default:
      return theme.palette.error.main;
  }
};

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'errorType',
})<{ errorType?: string | undefined }>(({ theme, errorType }) => ({
  '& .Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: getValidationColor(theme, errorType),
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: getValidationColor(theme, errorType),
  },
}));

const StyledFormHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== 'errorType',
})<{ errorType?: string | undefined }>(({ theme, errorType }) => ({
  '&.Mui-error': {
    color: getValidationColor(theme, errorType),
  },
}));

export type HookFormPlacesAutocompleteProps = {
  disabled?: boolean;
  permissionScope?: PermissionScope;
  boxSx?: SxProps<Theme>;
  helperText?: string;
  setValue: UseFormSetValue<FieldValues>;
  fieldMapping: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
} & TextFieldProps &
  HookFormComponentProps;

export const HookFormPlacesAutocomplete: FC<
  HookFormPlacesAutocompleteProps
> = ({
  errors,
  name = '',
  label,
  control,
  fullWidth = true,
  disabled = false,
  sx,
  boxSx,
  size = 'medium',
  permissionScope,
  helperText,
  setValue,
  fieldMapping,
  shouldUnregister = false,
}) => {
  const { isPermissionDisabled, getDisabledReason } = useAppAccessContext();
  const scriptStatus = useGoogleMapsScript();

  const permissionDisabled = isPermissionDisabled(permissionScope);
  const disabledCopy =
    permissionScope &&
    permissionDisabled &&
    getDisabledPermissionCopy(getDisabledReason(permissionScope));

  const errorType = _.get(errors, name)?.type as string | undefined;

  const {
    ready,
    value: autocompleteValue,
    setValue: setAutocompleteValue,
    suggestions: { status, data },
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ['address'],
    },
    debounce: 300,
    initOnMount: false,
  });

  useEffect(() => {
    if (scriptStatus === 'ready' && !ready) {
      init();
    }
  }, [scriptStatus, ready, init]);

  const previousReadyRef = useRef<boolean>(false);
  useEffect(() => {
    if (ready && !previousReadyRef.current && autocompleteValue) {
      setAutocompleteValue(autocompleteValue, true);
    }
    previousReadyRef.current = ready;
  }, [ready, autocompleteValue, setAutocompleteValue]);

  type PredictionOption = {
    placeId: string;
    description: string;
  };

  const predictions = useMemo<PredictionOption[]>(() => {
    if (status === 'OK' && data) {
      return data.map((prediction) => ({
        placeId: prediction.place_id,
        description: prediction.description,
      }));
    }
    return [];
  }, [status, data]);

  const handlePlaceSelect = async (placeId: string) => {
    try {
      const results = await getGeocode({ placeId });
      if (results && results[0]) {
        const place = results[0];
        const parsedAddress = parseAddressComponents(place);

        setValue(fieldMapping.line1, parsedAddress.line1);
        setValue(fieldMapping.line2, parsedAddress.line2);
        setValue(fieldMapping.city, parsedAddress.city);
        setValue(fieldMapping.state, parsedAddress.state);
        setValue(fieldMapping.postalCode, parsedAddress.postalCode);
        setValue(fieldMapping.country, parsedAddress.country);
      }
      clearSuggestions();
    } catch (error) {
      // We're okay silently failing here
    }
  };

  const isReady = scriptStatus === 'ready' && ready;
  const isLoading = scriptStatus === 'loading' || !isReady;
  const watchedValue = useWatch({ control, name });
  const previousValueRef = useRef<string>('');

  useEffect(() => {
    const currentValue = typeof watchedValue === 'string' ? watchedValue : '';
    if (
      currentValue &&
      currentValue !== previousValueRef.current &&
      currentValue !== autocompleteValue
    ) {
      setAutocompleteValue(currentValue);
      previousValueRef.current = currentValue;
    } else if (!currentValue && previousValueRef.current) {
      previousValueRef.current = '';
    }
  }, [watchedValue, autocompleteValue, setAutocompleteValue]);

  return (
    <Controller
      name={name}
      control={control}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, value, onBlur } }) => (
        <Tooltip
          title={
            permissionScope &&
            permissionDisabled &&
            disabledCopy &&
            `${disabledCopy?.message} ${disabledCopy?.action}`
          }
        >
          <Box sx={{ mb: 2, ...boxSx }}>
            <Autocomplete<PredictionOption, boolean, boolean, boolean>
              freeSolo
              options={predictions}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.description
              }
              inputValue={autocompleteValue}
              onInputChange={(_event, newValue) => {
                setAutocompleteValue(newValue);
                onChange(newValue);
              }}
              onChange={(_event, newValue) => {
                if (
                  typeof newValue === 'object' &&
                  newValue !== null &&
                  'placeId' in newValue
                ) {
                  handlePlaceSelect(newValue.placeId);
                  setAutocompleteValue(newValue.description);
                  onChange(newValue.description);
                } else if (typeof newValue === 'string') {
                  onChange(newValue);
                } else {
                  onChange('');
                  setAutocompleteValue('');
                }
              }}
              onBlur={onBlur}
              disabled={permissionDisabled || disabled || !isReady}
              loading={isLoading}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  variant="outlined"
                  fullWidth={fullWidth}
                  sx={sx}
                  size={size}
                  name={name}
                  error={!!_.get(errors, name)}
                  aria-describedby={`${name}Error`}
                  label={label}
                  errorType={errorType}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {helperText && (
              <StyledFormHelperText
                id={`${name}HelperText`}
                errorType={errorType}
              >
                {helperText}
              </StyledFormHelperText>
            )}
            {_.get(errors, name)?.message && (
              <StyledFormHelperText
                error
                id={`${name}Error`}
                errorType={errorType}
              >
                {(_.get(errors, name) as FieldError)?.message}
              </StyledFormHelperText>
            )}
          </Box>
        </Tooltip>
      )}
    />
  );
};
